import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaGraduationCap, FaStar, FaArrowRight, FaUsers} from 'react-icons/fa';

const Hero = () => {
    const [currentText, setCurrentText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const locations = ['Cotonou', 'Lomé', 'Niger', 'Abidjan', 'Dakar', 'Libreville','Casablanca','Paris'];
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseTime = 2000;

    useEffect(() => {
        const handleTyping = () => {
            const current = locations[currentIndex];

            if (!isDeleting) {
                if (currentText.length < current.length) {
                    setCurrentText(current.substring(0, currentText.length + 1));
                } else {
                    setTimeout(() => setIsDeleting(true), pauseTime);
                }
            } else {
                if (currentText.length > 0) {
                    setCurrentText(current.substring(0, currentText.length - 1));
                } else {
                    setIsDeleting(false);
                    setCurrentIndex((prev) => (prev + 1) % locations.length);
                }
            }
        };

        const timer = setTimeout(handleTyping, typingSpeed);
        return () => clearTimeout(timer);
    }, [currentText, isDeleting, currentIndex]);

    return (
        <>
            <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .hero-wrapper {
          font-family: inherit;
          margin-top: 80px;
          position: relative;
        }

        .hero {
          position: relative;
          background: linear-gradient(135deg, #f0fdf4 0%, #e8f8f5 50%, #d1f2eb 100%);
          overflow: hidden;
          padding: 60px 0 0;
        }

        /* Animated Background Particles */
        .hero::before {
          content: '';
          position: absolute;
          top: -20%;
          right: -10%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(25, 135, 84, 0.08) 0%, transparent 70%);
          border-radius: 50%;
          animation: float 25s ease-in-out infinite;
        }

        .hero::after {
          content: '';
          position: absolute;
          bottom: -20%;
          left: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(13, 202, 240, 0.06) 0%, transparent 70%);
          border-radius: 50%;
          animation: float 20s ease-in-out infinite reverse;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1) rotate(0deg);
          }
          33% {
            transform: translate(30px, -30px) scale(1.05) rotate(5deg);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.95) rotate(-5deg);
          }
        }

        .hero-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px 80px;
          position: relative;
          z-index: 1;
        }

        .hero-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }

        .hero-left {
          animation: slideInLeft 0.8s ease-out;
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Improved Badge with Glassmorphism */
        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          padding: 12px 24px;
          border-radius: 50px;
          box-shadow: 0 4px 20px rgba(25, 135, 84, 0.12), 0 0 0 1px rgba(25, 135, 84, 0.1);
          margin-bottom: 32px;
          font-size: 14px;
          font-weight: 600;
          color: #198754;
          border: 1px solid rgba(25, 135, 84, 0.15);
          animation: badgePulse 3s ease-in-out infinite;
        }

        @keyframes badgePulse {
          0%, 100% {
            box-shadow: 0 4px 20px rgba(25, 135, 84, 0.12), 0 0 0 1px rgba(25, 135, 84, 0.1);
          }
          50% {
            box-shadow: 0 6px 28px rgba(25, 135, 84, 0.18), 0 0 0 2px rgba(25, 135, 84, 0.15);
          }
        }

        .hero-badge-icon {
          font-size: 16px;
          color: #198754;
          animation: iconRotate 2s ease-in-out infinite;
        }

        @keyframes iconRotate {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.1) rotate(5deg); }
        }

        /* Enhanced Title with Animated Gradient */
        .hero-title {
          font-size: 56px;
          font-weight: 700;
          line-height: 1.15;
          color: #1a202c;
          margin-bottom: 20px;
          letter-spacing: -0.8px;
        }

        .hero-title-main {
          display: block;
          color: #2d3748;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .hero-title-gradient {
          background: linear-gradient(120deg, #198754 0%, #fd7e14 50%, #198754 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          display: block;
          font-weight: 800;
          animation: gradientShift 4s ease-in-out infinite;
        }

        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .hero-location-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 16px;
          font-size: 32px;
          font-weight: 600;
          color: #2d3748;
        }

        .hero-location-text {
          color: #4a5568;
          font-weight: 500;
        }

        .hero-location-dynamic {
          color: #fd7e14;
          min-width: 200px;
          display: inline-block;
          border-right: 3px solid #0dcaf0;
          padding-right: 4px;
          animation: blink 0.7s step-end infinite;
          text-shadow: 0 0 20px rgba(13, 202, 240, 0.3);
        }

        @keyframes blink {
          50% {
            border-color: transparent;
          }
        }

        .hero-subtitle {
          font-size: 18px;
          line-height: 1.7;
          color: #4a5568;
          margin-bottom: 40px;
          margin-top: 24px;
          font-weight: 400;
        }

        .hero-cta {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        /* Enhanced Buttons */
        .btn-hero {
          padding: 18px 36px;
          border: none;
          border-radius: 14px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          font-family: inherit;
          position: relative;
          overflow: hidden;
        }

        .btn-hero::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }

        .btn-hero:hover::before {
          width: 300px;
          height: 300px;
        }

        .btn-hero-primary {
          background: linear-gradient(135deg, #198754 0%, #0d6938 100%);
          color: white;
          box-shadow: 0 8px 24px rgba(25, 135, 84, 0.3);
        }

        .btn-hero-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(25, 135, 84, 0.4);
        }

        .btn-hero-secondary {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          color: #198754;
          border: 2px solid #198754;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
        }

        .btn-hero-secondary:hover {
          background: #198754;
          color: white;
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(25, 135, 84, 0.3);
        }

        .hero-right {
          position: relative;
          animation: slideInRight 0.8s ease-out;
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Image Container with Modern Effects */
        .hero-image-container {
          position: relative;
          border-radius: 24px;
          overflow: visible;
          padding: 15px;
          max-width: 90%;
          margin: 0 auto;
        }

        .hero-image-wrapper {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          transition: transform 0.4s ease;
          animation: imageFloat 6s ease-in-out infinite;
        }

        @keyframes imageFloat {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-10px) scale(1.02);
          }
        }

        .hero-image-wrapper:hover {
          transform: scale(1.03) !important;
        }

        .hero-image {
          width: 100%;
          height: auto;
          display: block;
          border-radius: 20px;
        }

        /* Glassmorphism Floating Cards */
        .hero-floating-card {
          position: absolute;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          padding: 20px 24px;
          border-radius: 16px;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          gap: 16px;
          animation: floatCard 4s ease-in-out infinite;
          border: 1px solid rgba(255, 255, 255, 0.5);
          transition: all 0.3s ease;
        }

        .hero-floating-card:hover {
          transform: translateY(-5px) scale(1.05);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
        }

        @keyframes floatCard {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-12px);
          }
        }

        .hero-card-1 {
          top: 5%;
          left: -10%;
          background: linear-gradient(135deg, rgba(25, 135, 84, 0.95) 0%, rgba(13, 105, 56, 0.95) 100%);
          backdrop-filter: blur(20px);
          color: white;
          animation-delay: 0s;
        }

        .hero-card-2 {
          bottom: 15%;
          right: -10%;
          animation-delay: 1s;
        }

        .hero-card-3 {
          top: 45%;
          left: -12%;
          animation-delay: 0.5s;
        }

        .hero-card-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          background: rgba(255, 255, 255, 0.25);
          transition: all 0.3s ease;
        }

        .hero-floating-card:hover .hero-card-icon {
          transform: rotate(10deg) scale(1.1);
        }

        .hero-card-2 .hero-card-icon {
          background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
          color: white;
        }

        .hero-card-3 .hero-card-icon {
          background: linear-gradient(135deg, #0dcaf0 0%, #0099cc 100%);
          color: white;
        }

        .hero-card-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .hero-card-title {
          font-size: 13px;
          font-weight: 600;
          opacity: 0.9;
        }

        .hero-card-value {
          font-size: 20px;
          font-weight: 700;
        }

        .hero-card-2 .hero-card-value {
          color: #198754;
        }

        .hero-card-3 .hero-card-value {
          color: #0dcaf0;
        }

        /* Wave */
        .hero-wave {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          overflow: hidden;
          line-height: 0;
        }

        .hero-wave svg {
          position: relative;
          display: block;
          width: calc(100% + 1.3px);
          height: 120px;
        }

        .hero-wave path {
          fill: #ffffff;
        }

        /* Responsive */
        @media (max-width: 968px) {
          .hero {
            padding: 50px 0 0;
          }

          .hero-container {
            padding-bottom: 70px;
          }

          .hero-content {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .hero-title {
            font-size: 44px;
          }

          .hero-location-wrapper {
            font-size: 26px;
          }

          .hero-subtitle {
            font-size: 17px;
          }

          .hero-floating-card {
            display: none;
          }

          .hero-image-container {
            padding: 10px;
            max-width: 100%;
          }
        }

        @media (max-width: 640px) {
          .hero-wrapper {
            margin-top: 70px;
          }

          .hero {
            padding: 40px 0 0;
          }

          .hero-container {
            padding: 0 16px 60px;
          }

          .hero-title {
            font-size: 36px;
          }

          .hero-location-wrapper {
            font-size: 22px;
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }

          .hero-location-dynamic {
            min-width: 150px;
          }

          .hero-subtitle {
            font-size: 16px;
            margin-bottom: 32px;
          }

          .hero-cta {
            flex-direction: column;
          }

          .btn-hero {
            width: 100%;
            justify-content: center;
          }

          .hero-wave svg {
            height: 100px;
          }
        }
      `}</style>

            <div className="hero-wrapper">
                <section className="hero">
                    <div className="hero-container">
                        <div className="hero-content">
                            {/* Left Content */}
                            <div className="hero-left">
                                <div className="hero-badge">
                                    <FaCheckCircle className="hero-badge-icon" />
                                    <span>Certificateur de compétences agréé</span>
                                </div>

                                <h1 className="hero-title">
                                    <span className="hero-title-main">Formation</span>
                                    <span className="hero-title-gradient">Professionnelle continue</span>
                                </h1>

                                <div className="hero-location-wrapper">
                                    <span className="hero-location-text">à</span>
                                    <span className="hero-location-dynamic">{currentText}</span>
                                </div>

                                <p className="hero-subtitle">
                                    CINEF met à votre disposition des formations professionnelles continues
                                    de qualité en présentiel, en e-learning et en blended learning pour
                                    développer vos compétences.
                                </p>

                                <div className="hero-cta">
                                    <a href="https://www.cinef-bj.com/formations/docs/catalogue_cinef_2026.pdf" target='_blank' rel="noopener noreferrer" className="btn-hero btn-hero-primary">
                                        Catalogue 2026
                                        <FaArrowRight />
                                    </a>
                                    <a href="/ledevis" className="btn-hero btn-hero-secondary">
                                        Demander un devis
                                    </a>
                                </div>
                            </div>

                            {/* Right Content - Image */}
                            <div className="hero-right">
                                <div className="hero-image-container">
                                    <div className="hero-image-wrapper">
                                        <img 
                                            src="../assets/images/Certificat.png" 
                                            alt="Formation professionnelle CINEF" 
                                            className="hero-image"
                                        />
                                    </div>

                                    {/* Floating Cards with Glassmorphism */}
                                    <div className="hero-floating-card hero-card-1">
                                        <div className="hero-card-icon">
                                            <FaGraduationCap />
                                        </div>
                                        <div className="hero-card-content">
                                            <div className="hero-card-title">Certifications</div>
                                            <div className="hero-card-value">Internationales</div>
                                        </div>
                                    </div>

                                    <div className="hero-floating-card hero-card-2">
                                        <div className="hero-card-icon">
                                            <FaStar />
                                        </div>
                                        <div className="hero-card-content">
                                            <div className="hero-card-title">Note moyenne</div>
                                            <div className="hero-card-value">4.8/5</div>
                                        </div>
                                    </div>

                                    <div className="hero-floating-card hero-card-3">
                                        <div className="hero-card-icon">
                                            <FaUsers />
                                        </div>
                                        <div className="hero-card-content">
                                            <div className="hero-card-title">Apprenants</div>
                                            <div className="hero-card-value">5000+</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Hero Wave */}
                    <div className="hero-wave">
                        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
                            <path d="M0,0 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,120 L0,120 Z" />
                        </svg>
                    </div>
                </section>
            </div>
        </>
    );
};

export default Hero;